import { Box, Typography } from '@mui/material';
import {
  DataGrid,
  getGridStringOperators,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid';
import React, { useCallback, useEffect, useMemo } from 'react';

import IntellioAdvantageFolderOptionsMenu from '../../components/Folder/FolderOptionsMenu/IntellioAdvantageFolderOptionsMenu';
import { StyledSkeleton } from '../../theme/CustomComponents';
import { Folder } from '../../Types/folder';
import { useProjects } from '../Providers/ProjectsProvider';

interface ProjectsTableProps {
  handleProjectClick: (id: string) => void;
}

const ProjectsTable: React.FC<ProjectsTableProps> = ({ handleProjectClick }) => {
  const {
    projectFolders,
    isLoading,
    projectCount,
    page,
    setPage,
    pageSize,
    setPageSize,
    filterModel,
    setFilterModel,
    setShouldFetch,
    setSortModel,
  } = useProjects();

  const customOperators = useMemo(
    () =>
      getGridStringOperators().filter((op) =>
        ['contains', 'equals', 'startsWith', 'endsWith'].includes(op.value)
      ),
    []
  );

  const columns = useMemo<GridColDef<Folder>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Project',
        flex: 2,
        minWidth: 300,
        sortable: projectCount > 0,
        filterable: projectCount > 0,
        disableColumnMenu: projectCount === 0,
        filterOperators: customOperators,
      },
      {
        field: 'status',
        headerName: 'Status',
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Typography>
            {row.project?.lastAnalysisRunTime
              ? 'Analysis'
              : row.project?.lastDiscoveryRunTime
              ? 'Discovery'
              : 'Not Started'}
          </Typography>
        ),
      },
      {
        field: 'clientName',
        headerName: 'Client',
        sortable: projectCount > 0,
        filterable: projectCount > 0,
        disableColumnMenu: projectCount === 0,
        filterOperators: customOperators,
      },
      {
        field: 'lastDiscoveryRunTime',
        headerName: 'Discovery Run',
        flex: 1,
        filterable: false,
        filterOperators: customOperators,
        sortable: projectCount > 0,
        disableColumnMenu: projectCount === 0,
        renderCell: ({ row }) => (
          <Typography align="center">
            {row.project?.lastDiscoveryRunTime &&
              new Date(row.project?.lastDiscoveryRunTime).toLocaleDateString()}
            <br />
            {row.project?.lastDiscoveryRunTime &&
              new Date(row.project?.lastDiscoveryRunTime).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
          </Typography>
        ),
      },
      {
        field: 'lastAnalysisRunTime',
        headerName: 'Analysis Run',
        flex: 1,
        filterable: false,
        filterOperators: customOperators,
        sortable: projectCount > 0,
        disableColumnMenu: projectCount === 0,
        renderCell: ({ row }) => (
          <Typography align="center">
            {row.project?.lastAnalysisRunTime &&
              new Date(row.project?.lastAnalysisRunTime).toLocaleDateString()}
            <br />
            {row.project?.lastAnalysisRunTime &&
              new Date(row.project?.lastAnalysisRunTime).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        disableColumnMenu: true,
        width: 50,
        renderCell: ({ row }) => {
          return <IntellioAdvantageFolderOptionsMenu folder={row} horizontalOptions={false} />;
        },
      },
    ],
    [customOperators, projectCount]
  );

  const handleSortChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      setShouldFetch(true);
    },
    [setSortModel, setShouldFetch]
  );

  const handleFilterChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);
      setPage(0);
      setShouldFetch(true);
    },
    [setFilterModel, setPage, setShouldFetch]
  );

  const handlePageChange = useCallback(
    (model: GridPaginationModel) => {
      setPage(model.page);
      setPageSize(model.pageSize);
      setShouldFetch(true);
    },
    [setPage, setPageSize, setShouldFetch]
  );

  useEffect(() => {
    setPageSize(10);
    setShouldFetch(true);
  }, [setPageSize, setShouldFetch]);

  const LoadingOverlay = useCallback(
    () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1,
          px: 1,
        }}
      >
        {[...Array(10)].map((_, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <StyledSkeleton variant="rectangular" width="35%" height={45} />
            <StyledSkeleton variant="rectangular" width="12%" height={45} />
            <StyledSkeleton variant="rectangular" width="12%" height={45} />
            <StyledSkeleton variant="rectangular" width="18%" height={45} />
            <StyledSkeleton variant="rectangular" width="18%" height={45} />
            <StyledSkeleton variant="circular" width={40} height={40} />
          </Box>
        ))}
      </Box>
    ),
    []
  );

  return (
    <DataGrid
      rows={isLoading ? [] : projectFolders}
      columns={columns}
      loading={isLoading}
      getRowId={(row) => row.id}
      rowCount={projectCount}
      sortingMode="server"
      filterMode="server"
      paginationMode="server"
      filterModel={filterModel}
      paginationModel={{ page, pageSize }}
      onPaginationModelChange={handlePageChange}
      onSortModelChange={handleSortChange}
      onFilterModelChange={handleFilterChange}
      onRowClick={(params) => handleProjectClick(params.row.id)}
      disableRowSelectionOnClick
      disableColumnSelector
      filterDebounceMs={300}
      slots={{
        loadingOverlay: LoadingOverlay,
      }}
      sx={{
        height: isLoading ? 635 : 631,
        '& .MuiDataGrid-row': {
          cursor: 'pointer',
        },
        '& .MuiDataGrid-overlay': {
          bgcolor: 'background.paper',
        },
      }}
    />
  );
};

export default ProjectsTable;
